// src/pages/SEOHelpers.tsx
import React from 'react';
import EnhancedSEO from '../components/common/EnhancedSEO';

interface PageSEOProps {
  title: string;
  description?: string;
  canonical?: string;
  keywords?: string[];
  image?: string;
  pageType?: 'home' | 'article' | 'product' | 'contact' | 'about' | 'pricing' | 'docs';
}

// Homepage SEO
export const HomeSEO: React.FC = () => (
  <EnhancedSEO
    title="AI-Powered Stock Research & Market Intelligence Platform"
    description="Discover the future of investing with Newsroom's AI-powered market intelligence platform. Get real-time insights, comprehensive data, and personalized investment recommendations."
    canonical="https://news-room.ca/"
    keywords={[
      "AI stock research",
      "market intelligence platform",
      "financial analysis AI",
      "investment insights",
      "real-time market data",
      "stock research assistant",
      "AI investing tools"
    ]}
    pageType="home"
    faqItems={[
      {
        question: "What is Newsroom?",
        answer: "Newsroom is an AI-powered stock research and market intelligence platform that helps investors make smarter decisions with real-time insights and comprehensive financial data."
      },
      {
        question: "How does Newsroom's AI technology work?",
        answer: "Newsroom's AI analyzes vast amounts of market data, news, financial reports, and technical indicators to deliver personalized investment insights and recommendations tailored to your goals."
      },
      {
        question: "Is Newsroom suitable for beginner investors?",
        answer: "Yes, Newsroom is designed for investors of all experience levels. Our intuitive interface and plain-language explanations make complex financial information accessible to everyone."
      }
    ]}
  />
);

// Features Page SEO
export const FeaturesSEO: React.FC = () => (
  <EnhancedSEO
    title="Features | AI Market Analysis & Stock Research Tools"
    description="Explore Newsroom's powerful AI-driven features for comprehensive stock research, market analysis, and investment insights. Discover how our platform helps you make better investment decisions."
    canonical="https://news-room.ca/features"
    keywords={[
      "stock research tools",
      "market analysis features",
      "AI trading assistant",
      "investment research platform",
      "financial data visualization",
      "stock screening tools",
      "technical analysis software"
    ]}
    pageType="product"
  />
);

// Pricing Page SEO
export const PricingSEO: React.FC = () => (
  <EnhancedSEO
    title="Pricing | Affordable AI Stock Research Plans"
    description="Choose the perfect Newsroom plan for your investment needs. From free access to premium features, we offer flexible pricing options for individual investors and financial professionals."
    canonical="https://news-room.ca/pricing"
    keywords={[
      "stock research pricing",
      "AI financial analysis subscription",
      "investment research plans",
      "market intelligence pricing",
      "affordable stock research",
      "financial data subscription"
    ]}
    pageType="pricing"
    productMeta={{
      price: "0",
      currency: "USD",
      availability: "InStock"
    }}
  />
);

// About Page SEO
export const AboutSEO: React.FC = () => (
  <EnhancedSEO
    title="About Newsroom | Our Mission & Story"
    description="Learn about Newsroom's mission to democratize financial intelligence with AI-powered market insights and stock research tools for investors of all levels."
    canonical="https://news-room.ca/about"
    keywords={[
      "Newsroom company",
      "financial AI mission",
      "stock research team",
      "market intelligence company",
      "fintech innovation",
      "investment research history"
    ]}
    pageType="about"
  />
);

// Contact Page SEO
export const ContactSEO: React.FC = () => (
  <EnhancedSEO
    title="Contact Us | Support & Inquiries"
    description="Get in touch with the Newsroom team for support, partnership inquiries, or feedback. We're here to help you make the most of our AI market intelligence platform."
    canonical="https://news-room.ca/contact"
    keywords={[
      "Newsroom contact",
      "financial AI support",
      "stock research help",
      "investment platform contact",
      "market intelligence support"
    ]}
    pageType="contact"
  />
);

// Documentation Page SEO
export const DocumentationSEO: React.FC = () => (
  <EnhancedSEO
    title="Documentation | Learn Newsroom's AI Research Platform"
    description="Comprehensive guides and resources for Newsroom's AI-powered stock research platform. Learn how to maximize your investment insights with our market intelligence tools."
    canonical="https://news-room.ca/docs"
    keywords={[
      "stock research documentation",
      "AI market analysis guides",
      "financial data tutorials",
      "investment platform help",
      "market intelligence documentation"
    ]}
    pageType="docs"
  />
);

// Generic SEO component for custom pages
export const PageSEO: React.FC<PageSEOProps> = ({
  title,
  description,
  canonical,
  keywords,
  image,
  pageType = 'article'
}) => (
  <EnhancedSEO
    title={title}
    description={description}
    canonical={canonical || `https://news-room.ca/${title.toLowerCase().replace(/\s+/g, '-')}`}
    keywords={keywords}
    ogImage={image}
    pageType={pageType}
  />
);
